import { useEffect, useState } from 'react';
import { useMeetingStore } from '../../../stores/meetingStore';
import { useSettingsStore } from '../../../stores/settingsStore';
import { useMediaPermissions } from '../../../hooks/useMediaPermissions';
import { logRoomActivity } from '../../../services/firebase/persistentRooms';
import {
  ConnectingState,
  ErrorState,
  MeetingContent,
} from '../../../pages/MeetingPageLayout';
import { PermissionsPrompt } from '../../meeting/PermissionsPrompt';
import type { PersistentRoom } from '../../../types/room';

interface RoomMeetingProps {
  room: PersistentRoom;
  user: { uid: string; displayName: string; avatarUrl?: string };
  onLeave: () => void;
}

/**
 * Active meeting view for a persistent room.
 */
export function RoomMeeting({ room, user, onLeave }: RoomMeetingProps) {
  const settings = useSettingsStore();
  const { isConnected, isConnecting, error, joinRoom, leaveRoom, clearError } = useMeetingStore();

  const {
    status,
    request,
    audioGranted,
    videoGranted,
    hasAudioDevice,
    hasVideoDevice,
  } = useMediaPermissions(settings.loadDevices);
  const [joinWithoutMedia, setJoinWithoutMedia] = useState(false);
  const canJoin = status === 'granted' || status === 'no-devices' || joinWithoutMedia;
  const allowAudio = audioGranted && !joinWithoutMedia;
  const allowVideo = videoGranted && !joinWithoutMedia;
  const [hasJoined, setHasJoined] = useState(false);

  useEffect(() => {
    if (!canJoin || hasJoined || isConnected || isConnecting || error) {
      return;
    }

    setHasJoined(true);
    joinRoom({
      roomCode: room.id,
      roomName: room.name,
      displayName: user.displayName,
      audioEnabled: allowAudio && !settings.isMuted,
      videoEnabled: allowVideo && settings.isCameraOn,
      avatarUrl: user.avatarUrl,
      isVR: false,
      microphoneDeviceId: allowAudio ? settings.microphoneDeviceId : undefined,
      cameraDeviceId: allowVideo ? settings.cameraDeviceId : undefined,
      speakerDeviceId: allowAudio ? settings.speakerDeviceId : undefined,
    }).then(() => {
      logRoomActivity(room.id, user.uid, user.displayName, 'entered');
    }).catch(() => {
      // Error handled by store
    });
  }, [
    canJoin,
    allowAudio,
    allowVideo,
    hasJoined,
    isConnected,
    isConnecting,
    error,
    joinRoom,
    room,
    user,
    settings,
  ]);

  useEffect(() => {
    if (!isConnected && !isConnecting && error) {
      const timer = setTimeout(() => {
        clearError();
        onLeave();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isConnected, isConnecting, error, clearError, onLeave]);

  useEffect(() => {
    return () => {
      if (isConnected) {
        leaveRoom();
      }
    };
  }, [isConnected, leaveRoom]);

  if (!canJoin) {
    return (
      <PermissionsPrompt
        status={status}
        hasAudioDevice={hasAudioDevice}
        hasVideoDevice={hasVideoDevice}
        onRequest={request}
        onContinueWithoutMedia={() => setJoinWithoutMedia(true)}
      />
    );
  }

  if (isConnecting) {
    return <ConnectingState />;
  }

  if (error) {
    return <ErrorState error={error} />;
  }

  const handleLeave = () => {
    logRoomActivity(room.id, user.uid, user.displayName, 'left');
    leaveRoom();
    onLeave();
  };

  return (
    <MeetingContent
      roomCode={room.id}
      roomName={room.name}
      onLeave={handleLeave}
    />
  );
}
